import {
  assignment_matches_role,
  assigned_user_name,
  assigned_user_role_name,
  assignment_summary_list,
  clamp_risk_score,
  normalize_assigned_person,
  normalize_audit_row,
  normalize_document,
  normalize_note,
  normalize_reasoning_item,
  normalize_text,
  normalize_time_journal,
  read_session_project_id,
  selected_application_id,
  to_number,
  unique_assigned_users,
} from './view_case_details_helpers';
import {
  as_array,
  build_risk_decision_options,
  default_display_people,
  decision_is_final_status,
  display_summary_row,
  display_tab,
  filtered_reassign_panel_members,
  lifecycle_step,
  selected_decision_status,
} from './view_case_details_computed_helpers';
import { display_sla_badge, display_sla_runtimes } from './view_case_details_sla_computed_helpers';
import { sensible_action_card } from './view_case_details_action_helpers';
import { time_journal_summary } from './view_case_details_time_computed_helpers';
import {
  time_entry_evidence_accept,
  time_entry_evidence_required,
  time_entry_max_file_size_label,
  time_entry_supported_formats_label,
} from './view_case_details_journal_methods';
import { NO_ANSWER_PROVIDED } from './view_case_details_answer_helpers';

function escape_regexp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function mention_names(options = []) {
  return as_array(options)
    .map((option) => normalize_text(typeof option === 'string' ? option : option.name))
    .filter(Boolean)
    .sort((left, right) => right.length - left.length);
}

function format_case_header_date(value = '') {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalize_text(value);

  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function mention_candidates(options = [], notes = []) {
  const names = [
    ...mention_names(options),
    ...as_array(notes).map((note) => normalize_text(note.author)),
  ].filter(Boolean);
  const seen = new Set();

  return names
    .filter((name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .sort((left, right) => right.length - left.length);
}

function note_body_segments(body = '', options = []) {
  const value = String(body || '');
  const names = mention_names(options);
  if (!names.length) {
    return [{ text: value, is_mention: false }];
  }

  const segments = [];
  const mention_pattern = new RegExp(`@(${names.map(escape_regexp).join('|')})(?=\\s|$|[.,;:!?])`, 'gi');
  let cursor = 0;
  let match = mention_pattern.exec(value);

  while (match) {
    if (match.index > cursor) {
      segments.push({ text: value.slice(cursor, match.index), is_mention: false });
    }

    segments.push({ text: match[0], is_mention: true });
    cursor = match.index + match[0].length;
    match = mention_pattern.exec(value);
  }

  if (cursor < value.length) {
    segments.push({ text: value.slice(cursor), is_mention: false });
  }

  return segments.length ? segments : [{ text: value, is_mention: false }];
}

function note_reply_meta(body = '') {
  const value = String(body || '');
  const match = /^\[ReplyTo:([^\]]+)]\s*/.exec(value);

  if (match) {
    return {
      is_reply: true,
      reply_to: normalize_text(match[1]),
      display_body: value.slice(match[0].length),
    };
  }

  return {
    is_reply: false,
    reply_to: '',
    display_body: value,
  };
}

function leading_mention_name(body = '', candidates = []) {
  const value = String(body || '');
  const matched_name = mention_names(candidates).find((name) => {
    const pattern = new RegExp(`^@${escape_regexp(name)}(?=\\s|$|[.,;:!?])`, 'i');
    return pattern.test(value);
  });

  if (matched_name) {
    return matched_name;
  }

  const match = /^@([^.,;:!?\n]+)/.exec(value);
  return normalize_text(match?.[1]);
}

function note_mentions_name(note = {}, name = '') {
  const mentioned_name = normalize_text(name).toLowerCase();
  if (!mentioned_name) {
    return false;
  }

  return String(note.body || '').toLowerCase().includes(`@${mentioned_name}`);
}

function is_legacy_reply(note = {}, notes = [], note_index = 0, candidates = []) {
  const reply_to = leading_mention_name(note.body, candidates);
  if (!reply_to) {
    return '';
  }

  const author = normalize_text(note.author).toLowerCase();
  const reply_to_key = reply_to.toLowerCase();

  return notes
    .slice(note_index + 1)
    .some((older_note) =>
      normalize_text(older_note.author).toLowerCase() === reply_to_key
      && note_mentions_name(older_note, author)
    )
    ? reply_to
    : '';
}

function original_note_for_reply(note = {}, notes = [], note_index = 0, reply_to = '', allow_author_fallback = false) {
  const author = normalize_text(note.author).toLowerCase();
  const reply_to_key = normalize_text(reply_to).toLowerCase();

  if (!author || !reply_to_key) {
    return null;
  }

  const older_notes_by_author = notes
    .slice(note_index + 1)
    .filter((older_note) => normalize_text(older_note.author).toLowerCase() === reply_to_key);

  return older_notes_by_author.find((older_note) => note_mentions_name(older_note, author))
    || (allow_author_fallback ? older_notes_by_author[0] : null)
    || null;
}

function mention_meta(body = '', candidates = []) {
  const mentioned_name = leading_mention_name(body, candidates);
  return {
    mentioned_name,
    clean_body: normalize_text(body),
  };
}

function note_identity(note = {}) {
  return String(note.id || `${note.author || 'note'}-${note.timestamp || ''}-${note.body || ''}`);
}

function note_thread_key(note = {}, index = 0) {
  return `${note_identity(note)}-${index}`;
}

function session_value(key = '') {
  if (typeof window === 'undefined') {
    return '';
  }

  return normalize_text(window.sessionStorage.getItem(key));
}

function email_local_part(email = '') {
  const value = normalize_text(email).toLowerCase();
  return value.includes('@') ? value.split('@')[0] : value;
}

function assigned_user_id(assignment = {}) {
  return to_number(
    assignment?.assigned_to_id
    || assignment?.user_id
    || assignment?.assigned_to?.id
  );
}

function assigned_user_email(assignment = {}) {
  return normalize_text(
    assignment?.assigned_to_email
    || assignment?.email
    || assignment?.assigned_to?.email
  );
}

function current_user_id(component) {
  return to_number(
    component.$store?.getters?.currentUserId
    || session_value('current_user_id')
    || session_value('user_id')
  );
}

function current_user_tokens(component) {
  const email = session_value('user_email') || session_value('email') || session_value('username');
  const values = [
    component.$store?.getters?.currentLeadName,
    session_value('current_lead_name'),
    session_value('lead_name'),
    session_value('user_full_name'),
    session_value('full_name'),
    email,
    email_local_part(email),
  ];

  return new Set(
    values
      .map((value) => normalize_text(value).toLowerCase())
      .filter(Boolean)
  );
}

function mention_option_from_assignment(assignment = {}, index = 0) {
  const name = assigned_user_name(assignment);
  const role = assigned_user_role_name(assignment);
  const user_id = assigned_user_id(assignment);
  const email = assigned_user_email(assignment);

  return {
    key: String(user_id || assignment?.id || `${name}-${index}`),
    user_id,
    email,
    name,
    detail: role,
  };
}

function mention_option_matches_current_user(component, option = {}) {
  const user_id = current_user_id(component);
  if (user_id && Number(option.user_id) === Number(user_id)) {
    return true;
  }

  const tokens = current_user_tokens(component);
  return [
    option.name,
    option.email,
    email_local_part(option.email),
  ]
    .map((value) => normalize_text(value).toLowerCase())
    .filter(Boolean)
    .some((value) => tokens.has(value));
}

const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function display_timestamp_value(value = '') {
  const normalized = normalize_text(value);
  const match = /^(\d{1,2})\s+([A-Za-z]{3,})\s*(?:-|,)?\s*(\d{1,2}):(\d{2})?$/.exec(normalized);

  if (!match) {
    return 0;
  }

  const day = Number(match[1]);
  const month = MONTH_INDEX[String(match[2]).slice(0, 3).toLowerCase()];
  const hour = Number(match[3] || 0);
  const minute = Number(match[4] || 0);

  if (!Number.isFinite(day) || month === undefined || !Number.isFinite(hour) || !Number.isFinite(minute)) {
    return 0;
  }

  return new Date(new Date().getFullYear(), month, day, hour, minute).getTime();
}

function parse_timestamp_value(value = '') {
  const display_value = display_timestamp_value(value);
  if (display_value) {
    return display_value;
  }

  const direct_value = Date.parse(value || '');
  if (Number.isFinite(direct_value)) {
    return direct_value;
  }

  return 0;
}

function timestamp_value(...values) {
  return values
    .map(parse_timestamp_value)
    .find((value) => Number.isFinite(value) && value > 0) || 0;
}

function feed_item_key(prefix = 'item', item = {}, index = 0) {
  return `${prefix}-${item.id || item.thread_key || item.when || item.timestamp || index}`;
}

function explicit_reasoning_feed_item(item = {}, index = 0) {
  const normalized_item = normalize_reasoning_item(item, index);
  const sort_time = timestamp_value(
    item?.date_created,
    item?.created_at,
    item?.journal_date,
    item?.timestamp
  );

  return {
    ...normalized_item,
    id: feed_item_key('reasoning', item, index),
    type: normalized_item.type || 'Decision',
    tag_class: normalized_item.tag_class || 'case-details-tag-human',
    sort_time,
    sort_index: index,
  };
}

function local_reasoning_feed_item(item = {}, index = 0) {
  return {
    id: feed_item_key('local-reasoning', item, index),
    date: item.date || '',
    time: item.time || '',
    type: item.type || 'Human',
    tag_class: item.tag_class || 'case-details-tag-human',
    body: normalize_text(item.body),
    sort_time: timestamp_value(item?.date_created, item?.created_at, item?.timestamp),
    sort_index: index,
  };
}

function note_feed_type(note = {}) {
  if (note.is_reply) {
    return 'Reply';
  }

  if (note.mentioned_name) {
    return 'Mention';
  }

  return 'Note';
}

function note_feed_body(note = {}) {
  const author = normalize_text(note.author) || 'Unknown user';
  const body = normalize_text(note.body);
  const reply_context = note.is_reply && note.reply_to ? `Reply to ${note.reply_to}: ` : '';

  return `${reply_context}${author}: ${body}`;
}

function note_feed_item(note = {}, index = 0) {
  const [date = '', time = ''] = String(note.timestamp || '').split(/\s+-\s+/);

  return {
    id: feed_item_key('note', note, index),
    date,
    time,
    type: note_feed_type(note),
    tag_class: 'case-details-tag-human',
    body: note_feed_body(note),
    sort_time: timestamp_value(note?.action_created_at, note?.created_at, note?.timestamp, `${date} ${time}`),
    sort_index: index,
  };
}

function audit_row_is_reasoning(row = {}) {
  const event = normalize_text(row.event).toLowerCase();
  return [
    'decision',
    'status',
    'transition',
    'moved',
    'assign',
    'reassign',
    'approve',
    'approved',
    'reject',
    'decline',
    'verify',
    'workflow',
  ].some((token) => event.includes(token));
}

function assigned_user_names(assignments = []) {
  const seen = new Set();

  return unique_assigned_users(as_array(assignments))
    .map((assignment) => assigned_user_name(assignment))
    .filter((name) => {
      const key = normalize_text(name).toLowerCase();
      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function replace_routed_count_with_names(event = '', routed_to_summary = '') {
  const body = normalize_text(event);
  const routed_to = normalize_text(routed_to_summary);

  if (!routed_to) {
    return body;
  }

  return body.replace(/Routed to \d+ user\(s\)\.?/gi, `Routed to ${routed_to}.`);
}

function audit_feed_item(row = {}, index = 0, routed_to_summary = '') {
  const audit_time_parts = String(row.when || '').split(/\s+/).filter(Boolean);
  const date = audit_time_parts.slice(0, 2).join(' ');
  const time = audit_time_parts.slice(2).join(' ');
  const event = replace_routed_count_with_names(row.event, routed_to_summary);

  return {
    id: feed_item_key('audit', row, index),
    date,
    time,
    type: 'Audit',
    tag_class: 'case-details-tag-system',
    body: `${row.actor || 'SYSTEM'}: ${event}`,
    sort_time: Number(row.sort_time || 0) || timestamp_value(row.when, `${date} ${time}`),
    sort_index: index,
  };
}

function reasoning_feed_sort(left = {}, right = {}) {
  const left_time = Number(left.sort_time || 0);
  const right_time = Number(right.sort_time || 0);
  if (left_time || right_time) {
    return (right_time - left_time) || Number(right.sort_index || 0) - Number(left.sort_index || 0);
  }

  return Number(right.sort_index || 0) - Number(left.sort_index || 0);
}

export default {
    page_loading() {
      return this.loading_case_facts
        || Boolean(this.saving_fact_section_key)
        || this.submitting_case_form
        || this.loading_decision_workflow
        || this.loading_documents
        || this.uploading_document
        || this.submitting_document_review
        || this.loading_time_entries
        || this.submitting_time_entry
        || this.loading_notes
        || this.submitting_note
        || this.loading_audit_logs
        || this.loading_reassign_users
        || this.submitting_reassign
        || this.submitting_decision;
    },
    page_loading_message() {
      if (this.submitting_case_form) {
        return 'Submitting form and calculating risk...';
      }

      if (this.saving_fact_section_key) {
        return 'Saving case details...';
      }

      return 'Loading case details...';
    },
    page_loading_detail() {
      if (this.submitting_case_form) {
        return 'Please wait while the completed answers are submitted for risk scoring.';
      }

      if (this.submitting_decision || this.loading_decision_workflow) {
        return 'Please wait while we prepare or save this decision.';
      }

      if (this.submitting_reassign || this.loading_reassign_users) {
        return 'Please wait while we load or update the assignment.';
      }

      if (this.loading_case_facts) {
        return 'Fetching submitted forms and answers for this case.';
      }

      if (this.saving_fact_section_key) {
        return 'Saving the case details and refreshing the submitted answers.';
      }

      if (this.loading_documents || this.uploading_document || this.submitting_document_review) {
        return 'Please wait while the document section updates.';
      }

      if (this.loading_time_entries || this.submitting_time_entry || this.loading_notes || this.submitting_note || this.loading_audit_logs) {
        return 'Refreshing the latest activity for this case.';
      }

      return 'Pulling the latest case information from the backend.';
    },
    has_case_facts() {
      return this.display_fact_columns.length > 0;
    },
    has_case_form() {
      return as_array(this.backend_application_forms).some((application_form) => Boolean(
        application_form?.form
        || application_form?.form_id
        || application_form?.application_form_id
      ));
    },
    case_form_facts() {
      return as_array(this.backend_fact_columns)
        .flatMap((column) => as_array(column?.items))
        .filter((fact) => fact?.application_form_id && fact?.section_question_id);
    },
    missing_required_case_form_facts() {
      return this.case_form_facts.filter((fact) => fact?.is_mandatory && (
        fact?.value === NO_ANSWER_PROVIDED || !normalize_text(fact?.value)
      ));
    },
    can_submit_case_form() {
      return this.has_case_form
        && Boolean(this.case_form_facts.length)
        && !this.missing_required_case_form_facts.length
        && !this.editing_fact_section_key
        && !this.saving_fact_section_key
        && !this.submitting_case_form;
    },
    verification_running() {
      return this.running_verification_keys.length > 0;
    },
    verification_has_run() {
      return this.verification_checks.some((check) => check?.has_run);
    },
    runnable_verification_checks() {
      return this.verification_checks.filter((check) => check?.runnable);
    },
    verification_run_button_label() {
      if (this.verification_running) return 'Running verification...';
      return this.verification_has_run ? 'Re-run verification' : 'Run verification';
    },
    display_case_code() {
      return this.$route.params.caseCode || 'C-2026-0142';
    },
    project_id() {
      return to_number(
        this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    display_case_meta() {
      const opened_at = format_case_header_date(this.backend_case_opened_at);
      return [
        this.display_case_code,
        opened_at ? `Opened ${opened_at}` : '',
      ].filter(Boolean).join(' - ');
    },
    display_case_title() {
      return [
        this.backend_domain_label,
        this.backend_what_happened_answer,
      ].filter(Boolean).join(' — ');
    },
    display_case_subtitle() {
      return [
        this.backend_submitter_name,
        ...as_array(this.backend_case_context_parts),
      ].filter(Boolean).join(' · ');
    },
    display_sla_badge() {
      return display_sla_badge(this);
    },
    display_sla_runtimes() {
      return display_sla_runtimes(this);
    },
    sensible_action_card() {
      return sensible_action_card(this);
    },
    selected_sensible_action() {
      return as_array(this.sensible_action_card.actions).find((action) =>
        action.key === this.action_decision_modal
      ) || {};
    },
    payout_action_is_override() {
      return Boolean(this.selected_sensible_action.key === 'payout' && this.selected_sensible_action.is_override);
    },
    display_fact_columns() {
      return as_array(this.backend_fact_columns);
    },
    display_documents() {
      return as_array(this.backend_documents).map((document, index) => normalize_document(document, index));
    },
    display_document_count() {
      return this.display_documents.length;
    },
    can_review_documents() {
      const role = normalize_text(this.$store?.getters?.currentRole || '').toLowerCase();
      return role.includes('coordinator')
        || role.includes('admin')
        || role.includes('case owner')
        || role.includes('case_owner')
        || role.includes('handler')
        || role === 'owner';
    },
    display_time_entries() {
      return as_array(this.backend_time_entries)
        .map((entry, index) => normalize_time_journal(entry, index))
        .filter((entry) => entry.activity || entry.hours_value);
    },
    time_journal_summary() {
      return time_journal_summary(this.display_time_entries);
    },
    time_entry_evidence_accept() {
      return time_entry_evidence_accept();
    },
    time_entry_evidence_formats_label() {
      return time_entry_supported_formats_label();
    },
    time_entry_evidence_max_size_label() {
      return time_entry_max_file_size_label();
    },
    time_entry_evidence_required() {
      return time_entry_evidence_required();
    },
    owner_summary_value() {
      const assigned_users = unique_assigned_users(as_array(this.backend_assigned_users));
      const owner_assignments = assigned_users.filter((assignment) =>
        assignment_matches_role(assignment, 'owner')
      );
      const fallback_assignments = assigned_users.filter((assignment) =>
        !assignment_matches_role(assignment, 'coordinator')
      );

      return assignment_summary_list(owner_assignments.length ? owner_assignments : fallback_assignments) || 'Unassigned';
    },
    coordinator_summary_value() {
      const coordinator_assignments = unique_assigned_users(as_array(this.backend_assigned_users)).filter((assignment) =>
        assignment_matches_role(assignment, 'coordinator')
      );

      return assignment_summary_list(coordinator_assignments) || 'Unassigned';
    },
    display_summary_rows() {
      return as_array(this.summary_rows).map((row) => display_summary_row(this, row));
    },
    display_people() {
      const assigned_users = unique_assigned_users(as_array(this.backend_assigned_users));
      if (!assigned_users.length) {
        return default_display_people();
      }

      return assigned_users.map(normalize_assigned_person);
    },
    display_signal_pills() {
      return as_array(this.signal_pills);
    },
    display_signal_timeline() {
      return as_array(this.signal_timeline);
    },
    display_notes() {
      const normalized_notes = as_array(this.backend_notes).map((note, index) => normalize_note(note, index));
      const candidates = mention_candidates(this.all_note_mention_options, normalized_notes);

      return normalized_notes
        .map((normalized_note, index) => {
          const explicit_reply_meta = note_reply_meta(normalized_note.body);
          const legacy_reply_to = explicit_reply_meta.is_reply
            ? ''
            : is_legacy_reply(normalized_note, normalized_notes, index, candidates);
          const reply_meta = legacy_reply_to
            ? { is_reply: true, reply_to: legacy_reply_to, display_body: normalized_note.body }
            : explicit_reply_meta;
          const original_note = original_note_for_reply(
            normalized_note,
            normalized_notes,
            index,
            reply_meta.reply_to,
            explicit_reply_meta.is_reply
          );
          const display_meta = mention_meta(reply_meta.display_body, candidates);
          const original_display_meta = mention_meta(original_note?.body || '', candidates);

          return {
            ...normalized_note,
            ...reply_meta,
            mentioned_name: display_meta.mentioned_name,
            clean_body: display_meta.clean_body,
            original_note_id: original_note?.id || '',
            original_author: original_note?.author || reply_meta.reply_to,
            original_body: original_display_meta.clean_body,
            body: reply_meta.display_body,
            body_segments: note_body_segments(reply_meta.display_body, candidates),
          };
        })
        .filter((note) => note.body);
    },
    display_note_threads() {
      const indexed_notes = this.display_notes.map((note, index) => ({
        ...note,
        thread_key: note_thread_key(note, index),
        display_index: index,
      }));
      const notes_by_id = new Map(indexed_notes.map((note) => [String(note.id), note]));
      const replies_by_parent = new Map();
      const attached_reply_ids = new Set();

      indexed_notes.forEach((note) => {
        const parent = note.is_reply && note.original_note_id
          ? notes_by_id.get(String(note.original_note_id))
          : null;

        if (!parent) {
          return;
        }

        const parent_key = note_identity(parent);
        const replies = replies_by_parent.get(parent_key) || [];
        replies.push(note);
        replies_by_parent.set(parent_key, replies);
        attached_reply_ids.add(note_identity(note));
      });

      return indexed_notes
        .filter((note) => !attached_reply_ids.has(note_identity(note)))
        .map((note) => {
          const replies = replies_by_parent.get(note_identity(note)) || [];
          const latest_index = [note, ...replies]
            .map((item) => Number(item.display_index))
            .reduce((latest, value) => Math.min(latest, value), Number(note.display_index));

          return {
            key: note.thread_key,
            note,
            replies,
            latest_index,
          };
        })
        .sort((left, right) => Number(left.latest_index) - Number(right.latest_index));
    },
    all_note_mention_options() {
      const seen = new Set();

      return unique_assigned_users(as_array(this.backend_assigned_users))
        .map(mention_option_from_assignment)
        .filter((option) => {
          const key = option.key || option.name;
          if (!option.name || seen.has(key)) {
            return false;
          }

          seen.add(key);
          return true;
        });
    },
    note_mention_options() {
      return as_array(this.all_note_mention_options)
        .filter((option) => !mention_option_matches_current_user(this, option));
    },
    filtered_note_mention_options() {
      const query = normalize_text(this.note_mention_query).toLowerCase();
      const options = as_array(this.note_mention_options);

      if (!query) {
        return options;
      }

      return options.filter((person) =>
        [person.name, person.detail]
          .map(normalize_text)
          .join(' ')
          .toLowerCase()
          .includes(query)
      );
    },
    new_note_body_segments() {
      return note_body_segments(this.new_note_body, this.all_note_mention_options);
    },
    display_audit_rows() {
      const routed_to_summary = assigned_user_names(this.backend_assigned_users).join(', ');

      return as_array(this.backend_audit_rows).map((row, index) => {
        const audit_row = normalize_audit_row(row, index);
        return {
          ...audit_row,
          event: replace_routed_count_with_names(audit_row.event, routed_to_summary),
        };
      });
    },
    display_reasoning_items() {
      const routed_to_summary = assigned_user_names(this.backend_assigned_users).join(', ');

      return [
        ...as_array(this.backend_reasoning_items).map(explicit_reasoning_feed_item),
        ...as_array(this.reasoning_items).map(local_reasoning_feed_item),
        ...as_array(this.display_notes).map(note_feed_item),
        ...as_array(this.display_audit_rows)
          .filter(audit_row_is_reasoning)
          .map((row, index) => audit_feed_item(row, index, routed_to_summary)),
      ]
        .filter((item) => item.body)
        .sort(reasoning_feed_sort);
    },
    display_tabs() {
      return as_array(this.tabs).map((tab) => display_tab(this, tab));
    },
    selected_application_id() {
      return to_number(this.$route?.query?.application_id || this.$route?.query?.applicationId)
        || selected_application_id(this.display_case_code);
    },
    selected_entity_profile_id() {
      return to_number(this.$route?.query?.entity_profile_id || this.$route?.query?.entityProfileId)
        || to_number(this.resolved_entity_profile_id);
    },
    selected_entity_type_id() {
      return to_number(this.$route?.query?.entity_type_id || this.$route?.query?.entityTypeId)
        || to_number(this.resolved_entity_type_id);
    },
    selected_risk_assessment_id() {
      return to_number(
        this.$route?.query?.risk_assessment_id
        || this.$route?.query?.riskAssessmentId
        || this.$route?.query?.assessment_id
        || this.$route?.query?.assessmentId
      );
    },
    risk_progress_width() {
      return `${clamp_risk_score(this.risk_panel?.score)}%`;
    },
    display_risk_band() {
      const band = normalize_text(this.risk_panel?.band);
      return band ? `${band} risk band` : '';
    },
    display_risk_drivers() {
      return Array.isArray(this.risk_panel?.drivers) ? this.risk_panel.drivers : [];
    },
    risk_decision_marker() {
      if (!this.has_risk_panel) {
        return 1;
      }

      const score = clamp_risk_score(this.risk_panel?.score);

      if (score >= 67) {
        return 0;
      }

      if (score >= 34) {
        return 1;
      }

      return 2;
    },
    risk_decision_options() {
      return build_risk_decision_options(this.risk_decision_marker);
    },
    risk_decision_guidance() {
      const active_option = this.risk_decision_options.find((option) => option.active);
      const score = this.has_risk_panel ? `${clamp_risk_score(this.risk_panel?.score)}/100` : 'pending';
      const band = this.display_risk_band || 'risk band pending';

      return `${active_option?.label || 'Review'} - ${band}, score ${score}`;
    },
    risk_decision_description() {
      return this.risk_decision_options.find((option) => option.active)?.description || '';
    },
    has_risk_panel() {
      return Boolean(this.risk_panel);
    },
    current_status_label() {
      const status_row = as_array(this.summary_rows).find((row) => row.is_status);

      return normalize_text(status_row?.value || '');
    },
    current_workflow_status_filter_id() {
      return to_number(
        this.$route?.query?.assigned_form_status_id
        || this.$route?.query?.current_status_id
        || this.$route?.query?.status_id
      );
    },
    ordered_workflow_statuses() {
      return as_array(this.workflow_statuses)
        .slice()
        .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));
    },
    current_workflow_status() {
      if (this.current_workflow_status_filter_id) {
        const status_by_id = this.ordered_workflow_statuses.find((status) =>
          Number(status.id) === Number(this.current_workflow_status_filter_id)
          || Number(status.status_id) === Number(this.current_workflow_status_filter_id)
        );

        if (status_by_id) {
          return status_by_id;
        }
      }

      const current_label = this.current_status_label.toLowerCase();

      return this.ordered_workflow_statuses.find((status) =>
        normalize_text(status.name).toLowerCase() === current_label
      ) || null;
    },
    current_workflow_status_index() {
      return this.ordered_workflow_statuses.findIndex((status) =>
        Number(status.id) === Number(this.current_workflow_status?.id)
      );
    },
    valid_next_status_ids() {
      if (!this.current_workflow_status) {
        return [];
      }

      return as_array(this.workflow_transition_rules)
        .filter((rule) =>
          rule.is_active
          && Number(rule.from_status_id) === Number(this.current_workflow_status.id)
          && rule.to_status_id
        )
        .map((rule) => Number(rule.to_status_id));
    },
    valid_next_statuses() {
      const allowed_ids = new Set(this.valid_next_status_ids);
      return this.ordered_workflow_statuses.filter((status) => allowed_ids.has(Number(status.id)));
    },
    primary_next_workflow_status() {
      return this.valid_next_statuses[0]
        || this.ordered_workflow_statuses[this.current_workflow_status_index + 1]
        || null;
    },
    lifecycle_stage_summary() {
      const total = this.ordered_workflow_statuses.length;
      if (!total || this.current_workflow_status_index < 0) {
        return this.risk_decision_guidance;
      }

      const current = this.current_workflow_status_index + 1;
      const next_label = this.primary_next_workflow_status?.name
        ? `Next: ${this.primary_next_workflow_status.name}`
        : 'Final stage';

      return `${this.risk_decision_guidance} - Stage ${current} of ${total}, ${next_label}`;
    },
    lifecycle_steps() {
      const current_id = Number(this.current_workflow_status?.id || 0);
      const next_id = Number(this.primary_next_workflow_status?.id || 0);

      return this.ordered_workflow_statuses.map((status, index) =>
        lifecycle_step(status, index, current_id, next_id, this.current_workflow_status_index)
      );
    },
    selected_decision_status() {
      return selected_decision_status(this);
    },
    decision_is_final_status() {
      return decision_is_final_status(this);
    },
    show_document_preview() {
      return Boolean(this.selected_document);
    },
    decision_modal_subtitle() {
      return `${this.display_case_title} - Current status: ${this.current_status_label || 'Unknown'}`;
    },
    filtered_reassign_panel_members() {
      return filtered_reassign_panel_members(this);
    },
    selected_reassign_assignments() {
      const selected_users = as_array(this.selected_reassign_users);
      return as_array(this.reassign_panel_members)
        .filter((member) => selected_users.includes(member.key))
        .map((member) => ({
          user_id: member.user_id,
          role_id: member.role_id,
        }))
        .filter((assignment) => assignment.user_id && assignment.role_id);
    }
};
